import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "@/api/client";
import { listEmployeesFull } from "@/api/lookups";
import { getMatching, isMatchingUnavailable } from "@/api/matching";
import { getMeetingCard, getMeetingTimeline } from "@/api/meetings";
import { isMatchingV2 } from "@/api/types";
import type {
  EmployeeSummary,
  MatchingResponse,
  MatchingV2Response,
  MeetingCard as MeetingCardDTO,
  UUID,
} from "@/api/types";
import { useActiveFolderStore } from "@/stores/activeFolder";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MeetingChatBlock } from "@/components/meeting-card/MeetingChatBlock";
import { MeetingEventStream } from "@/lib/sse-client";
import { ArtifactsTab } from "@/pages/meeting-card/ArtifactsTab";
import { CardHeaderBlock } from "@/pages/meeting-card/CardHeader";
import { MatchingTab } from "@/pages/meeting-card/MatchingTab";
import { ParticipantsTab } from "@/pages/meeting-card/ParticipantsTab";
import {
  PipelineProgressBlock,
  type PhaseState,
} from "@/pages/meeting-card/PipelineProgressBlock";
import { TranscriptTab } from "@/pages/meeting-card/TranscriptTab";
import { VersionsTab } from "@/pages/meeting-card/VersionsTab";
import { pickLatest } from "@/pages/meeting-card/helpers";

export interface MeetingCardViewProps {
  meetingId: UUID;
}

export function MeetingCardView({ meetingId }: MeetingCardViewProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const activeFolderId = useActiveFolderStore((s) => s.activeFolderId);
  const card = useQuery({
    queryKey: ["meeting", meetingId],
    queryFn: () => getMeetingCard(meetingId),
  });
  // matching-v2 §7.2 — the inline transcript «Не в участниках?» chip needs
  // the full employee registry so the operator can both confirm the picked
  // employee already exists and POST /participants/add for a new one. We
  // scope by the user's active folder (papki-tenants v3) so a user with
  // visibility on a subset of registries only sees their slice; the
  // backend re-applies the same filter on the actual mutation so this is
  // a UX hint, not a security boundary.
  const employeesQuery = useQuery<EmployeeSummary[]>({
    queryKey: ["employees", activeFolderId],
    queryFn: async () => {
      const rows = await listEmployeesFull({}, activeFolderId);
      return rows.map((r) => ({
        id: r.id,
        full_name: r.full_name,
        aliases: r.aliases,
      }));
    },
  });
  const addParticipantMutation = useMutation({
    mutationFn: (employeeId: UUID) =>
      apiFetch<MeetingCardDTO>(`/api/meetings/${meetingId}/participants/add`, {
        method: "POST",
        body: { employee_id: employeeId },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["meeting", meetingId] });
      void queryClient.invalidateQueries({
        queryKey: ["meeting-matching", meetingId],
      });
    },
  });
  const handleAddParticipant = useCallback(
    async (employeeId: UUID): Promise<void> => {
      await addParticipantMutation.mutateAsync(employeeId);
    },
    [addParticipantMutation],
  );
  // Timeline is a separate query so SSE updates do not refetch the heavy
  // artifacts/versions payload on every phase transition (T2). The 5-step
  // indicator is small enough that refetching it on each event is cheap.
  const timeline = useQuery({
    queryKey: ["meeting-timeline", meetingId],
    queryFn: () => getMeetingTimeline(meetingId),
  });
  // Phase 6 matching — top-level query so the tab badge can show the
  // pending count without forcing the tab to be activated. A 404 (backend
  // phase-6 not deployed) is treated as "no matching available" rather
  // than as an error: the badge silently hides and the tab shows the
  // empty-state body.
  const matching = useQuery<MatchingResponse>({
    queryKey: ["matching", meetingId],
    queryFn: () => getMatching(meetingId),
    retry: (_count, err) => !isMatchingUnavailable(err),
  });
  // matching-v2 (docs/api/matching-v2.contract.md). Same path; the BE
  // serves the v2 shape only when `FEATURE_MATCHING_V2=true`. We type the
  // query as the union so existing v1 callers (MatchingTab) keep working
  // against `["matching", ...]` while the v2 UX (inline badges in
  // TranscriptTab) reads through `["meeting-matching", ...]`. The
  // discriminator `isMatchingV2` decides which UI to surface; absence of
  // `transcript_revision` falls back to v1.
  const matchingV2Query = useQuery<MatchingResponse | MatchingV2Response>({
    queryKey: ["meeting-matching", meetingId],
    queryFn: () =>
      getMatching(meetingId) as Promise<MatchingResponse | MatchingV2Response>,
    retry: (_count, err) => !isMatchingUnavailable(err),
  });
  const matchingV2Snapshot: MatchingV2Response | null = isMatchingV2(
    matchingV2Query.data,
  )
    ? matchingV2Query.data
    : null;
  const versions = useMemo(
    () => card.data?.versions ?? [],
    [card.data?.versions],
  );
  const [activeVersionId, setActiveVersionId] = useState<UUID | null>(null);
  // v1.1.4 — citation-jump coordination. Clicking a citation timestamp on
  // the Artifacts tab switches `activeTab` to "transcript" and seeds
  // `transcriptHighlightMs`, which is consumed by `TranscriptTab` to
  // scroll + flash-highlight the matching utterance. `jumpTick` bumps on
  // every jump so the same target can be re-triggered without prop
  // re-equality short-circuits.
  const [activeTab, setActiveTab] = useState<string>("artifacts");
  const [transcriptHighlightMs, setTranscriptHighlightMs] = useState<
    number | null
  >(null);
  const [jumpTick, setJumpTick] = useState(0);

  const handleJumpToTranscript = useCallback((startMs: number) => {
    setActiveTab("transcript");
    setTranscriptHighlightMs(startMs);
    setJumpTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (versions.length === 0) {
      setActiveVersionId(null);
      return;
    }
    const latest = pickLatest(versions);
    setActiveVersionId((prev) => prev ?? latest?.id ?? null);
  }, [versions]);

  // SSE subscription — invalidate BOTH the card and the timeline on every
  // phase event so the 5-step indicator updates in real time.
  useEffect(() => {
    const stream = new MeetingEventStream();
    stream.start(meetingId, () => {
      void queryClient.invalidateQueries({ queryKey: ["meeting", meetingId] });
      void queryClient.invalidateQueries({
        queryKey: ["meeting-timeline", meetingId],
      });
    });
    return () => stream.close();
  }, [meetingId, queryClient]);

  // Rules of Hooks: every `useMemo` MUST run before any conditional
  // early-return, otherwise re-renders with a different code path
  // (loading → loaded → error) throw "Rendered more hooks than during
  // the previous render". Compute the registry / participants split here
  // so the hook order is stable; the values are unused in the loading
  // and error branches and the closures are cheap.
  const registry = useMemo(
    () => employeesQuery.data ?? [],
    [employeesQuery.data],
  );
  const participantIds = useMemo(
    () => new Set(card.data?.participants ?? []),
    [card.data?.participants],
  );
  const participantEmployees = useMemo(
    () => registry.filter((e) => participantIds.has(e.id)),
    [registry, participantIds],
  );

  if (card.isLoading) {
    return <p>{t("meetings.card.loading")}</p>;
  }
  if (card.isError || card.data === undefined) {
    return (
      <p role="alert" className="text-destructive">
        {t("meetings.card.loadError")}
      </p>
    );
  }
  const dto = card.data;
  const activeVersion =
    versions.find((v) => v.id === activeVersionId) ?? pickLatest(versions);

  // The FSM (ADR-003) is authoritative for the consensus phase: if the
  // latest version is `partial`, paint phase 5 yellow regardless of
  // whatever the event log shows (race conditions between the orchestrator
  // error event and the partial-state finalizer).
  const latest = pickLatest(versions);
  const consensusOverride: PhaseState | undefined =
    latest?.status === "partial" ? "partial" : undefined;

  return (
    <div className="space-y-4" data-testid="meeting-card">
      <CardHeaderBlock
        card={dto}
        activeVersion={activeVersion}
        versions={versions}
        onChangeVersion={setActiveVersionId}
      />
      <PipelineProgressBlock
        events={timeline.data?.events ?? []}
        consensusOverride={consensusOverride}
        isLoading={timeline.isLoading}
      />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="artifacts">
            {t("meetings.card.tabs.artifacts")}
          </TabsTrigger>
          <TabsTrigger value="transcript">
            {t("meetings.card.tabs.transcript")}
          </TabsTrigger>
          <TabsTrigger value="participants">
            {t("meetings.card.tabs.participants")}
          </TabsTrigger>
          <TabsTrigger value="matching" data-testid="tab-matching">
            <span>{t("meetings.card.tabs.matching")}</span>
            {matching.data !== undefined && matching.data.pending_count > 0 ? (
              <span
                className="ml-2 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-100 px-1 text-[10px] font-semibold text-amber-700"
                aria-label={t("meetings.card.matching.pendingBadgeAria", {
                  count: matching.data.pending_count,
                })}
                data-testid="matching-pending-badge"
              >
                {matching.data.pending_count}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="versions">
            {t("meetings.card.tabs.versions")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="artifacts">
          <ArtifactsTab
            version={activeVersion}
            onJumpToTranscript={handleJumpToTranscript}
            matching={matching.data ?? null}
          />
        </TabsContent>
        <TabsContent value="transcript">
          <TranscriptTab
            card={dto}
            highlightStartMs={transcriptHighlightMs}
            jumpTick={jumpTick}
            matchingV2={matchingV2Snapshot}
            participants={participantEmployees}
            registry={registry}
            onAddParticipant={handleAddParticipant}
          />
        </TabsContent>
        <TabsContent value="participants">
          <ParticipantsTab card={dto} />
        </TabsContent>
        <TabsContent value="matching">
          <MatchingTab meetingId={meetingId} />
        </TabsContent>
        <TabsContent value="versions">
          <VersionsTab versions={versions} />
        </TabsContent>
      </Tabs>
      <MeetingChatBlock meetingId={meetingId} />
    </div>
  );
}
